/// <amd-module name="VC/Stats/CodeMetricsSection" />

import * as React from "react";
import { Icon } from "VSSUI/Icon";
import { css } from "VSSUI/Util";
import { format } from "VSS/Core/Util/String";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssComponentContext, VssComponent, Components, IVssComponentProperties } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";

import * as Resources from "VC/Stats/Resources";

interface CodeMetricsData {
    gitmetrics?: GitCodeMetricsData;
    tfvcmetrics?: TfvcCodeMetricsData;
}

interface GitCodeMetricsData {
    authorsCount: number;
    commitsPushedCount: number;
    commitsTrend: number[];
    pullRequestsCompletedCount: number;
    pullRequestsCreatedCount: number;
}

interface TfvcCodeMetricsData {
    authors: number;
    changesets: number;
    changesetsTrend: number[];
}

const enum CodePerfConstants {
    Area = "CodeMetricsSection",
    Scenario = "FetchCodeMetrics"
}

interface CodeMetricsSectionProps extends IVssComponentProperties {
    numOfDays: number;
}

export class CodeMetricsSection extends VssComponent<CodeMetricsSectionProps, CodeMetricsData> {
    private readonly _serviceName: string | undefined;

    constructor(props: CodeMetricsSectionProps, context: IVssComponentContext) {
        super(props, context);

        const serviceInfo = context.pageContext.getService<IVssContributionService>("IVssContributionService")
            .getContribution<{ name: string; }>("ms.vss-code-web.code-service-navigation-info");

        if (serviceInfo) {
           this._serviceName = serviceInfo.name;
        }

        this.state = {};
    }

    public componentDidMount(): void {
        super.componentDidMount();
        this._fetchCodeMetrics(this.props.numOfDays);
    }

    public componentDidUpdate(prevProps: CodeMetricsSectionProps, prevState: CodeMetricsData): void {
        super.componentDidUpdate(prevProps, prevState);
        if (prevProps.numOfDays !== this.props.numOfDays) {
            this._fetchCodeMetrics(this.props.numOfDays);
        }
    }

    public render(): JSX.Element {
        return (
            <div className={css(this.props.className, "code-metric-tile")}>
                {(this.state.gitmetrics || this.state.tfvcmetrics) && this._serviceName ? (
                    <CodeMetricsContainer metricData={this.state} title={this._serviceName} />
                ) : null}
            </div>
        );
    }

    private _fetchCodeMetrics(numOfDays: number): void {
        const PROVIDER_ID = "ms.vss-code-web.code-metrics-data-provider-verticals";
        const performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        const properties = {
            numOfDays: numOfDays
        };

        performanceService.startScenario(CodePerfConstants.Scenario, false);
        this.trackPromise(
            this.context.pageContext
                .getService<IVssContributionService>("IVssContributionService")
                .getDataAsync<CodeMetricsData>(PROVIDER_ID, properties)
        ).promise.then(
            (data: CodeMetricsData | undefined) => {
                if (data) {
                    performanceService.endScenario(CodePerfConstants.Area, CodePerfConstants.Scenario, false);
                    this.setState(data);
                } else {
                    this._endPerfScenario(performanceService);
                }
            },
            () => {
                this._endPerfScenario(performanceService);
            }
        );
    }

    private _endPerfScenario(performanceService: IVssPerformanceService): void {
        performanceService.endScenario(CodePerfConstants.Area, CodePerfConstants.Scenario, false, undefined, { status: "failed" });
    }
}

const CodeMetricsContainer = (props: { metricData: CodeMetricsData; title: string }): JSX.Element => {
    const { tfvcmetrics, gitmetrics } = props.metricData;

    let commitsAndChangeSetsCount = 0;
    let commitsAndChangesetsString: string | undefined;

    if (tfvcmetrics && gitmetrics) {
        commitsAndChangeSetsCount = tfvcmetrics.changesets + gitmetrics.commitsPushedCount;
        const authorCount = gitmetrics.authorsCount + tfvcmetrics.authors;
        commitsAndChangesetsString = format(Resources.CommitsAndChangeSets, authorCount);
    } else if (tfvcmetrics) {
        commitsAndChangeSetsCount = tfvcmetrics.changesets;
        commitsAndChangesetsString = format(Resources.ChangeSetsByAuthorsString, tfvcmetrics.authors);
    } else if (gitmetrics) {
        commitsAndChangeSetsCount = gitmetrics.commitsPushedCount;
        commitsAndChangesetsString = format(Resources.CommitsByAuthorsString, gitmetrics.authorsCount);
    }

    return (
        <>
            <div className="body-l">{props.title}</div>
            <div className="code-stats flex-row">
                {gitmetrics &&
                    getComponent(
                        "pull-request-created code-stats-component",
                        "BranchMerge",
                        gitmetrics.pullRequestsCreatedCount,
                        Resources.PullRequestsOpened
                    )}
                {getComponent("commits-and-changesets code-stats-component", "BranchCommit", commitsAndChangeSetsCount, commitsAndChangesetsString)}
            </div>
        </>
    );
};

const getComponent = (containerClassName: string, iconName: string, count: number, text?: string): JSX.Element => {
    return (
        <div className={css(containerClassName, "flex-row flex-grow")}>
            <Icon iconName={iconName} className={"code-icon body-l"} />
            <div className="count-and-text">
                <div className="title-xs"> {count > 10000 ? Resources.MoreThan10K : count} </div>
                <div className="text body-s word-break">{text}</div>
            </div>
        </div>
    );
};

Components.add("VC.CodeStats", CodeMetricsSection);
